import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { differenceInCalendarDays, format, subDays, subHours } from "date-fns";

import { apiGet, apiPost, mockFor, mockResponse, queryKey } from "./client";
import {
  officesFingerprint,
  officesSettled,
  officeDataAvailable,
  isAllOffices,
  liveOffices,
  useOfficeMapping,
  type OfficeMapping,
} from "./office-mapping";
import type {
  ExceptionLogRow,
  ExceptionsLogSummary,
  RiskTier,
  RuleId,
} from "./types";
// Single source of truth for the rule catalog — owned by the Policy
// Compliance page (step 4); re-exported here so ExceptionsLog.tsx can keep
// importing it from this file.
import { RULE_DEFINITIONS } from "./policy-compliance";
export { RULE_DEFINITIONS };

const LOG_QUERY_SCOPE = "exceptions-log-summary";

/** Deterministic PRNG so the log is stable across renders and refetches. */
function makeRandom(seed: number) {
  let state = seed;
  return () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
}

/**
 * Relative rule frequency, broadly consistent with the story Policy
 * Compliance already tells: B1b dominates, Tier A is rare, B/C in between.
 * DQ rows are prerequisite-gate exclusions, so they stay light.
 */
const RULE_WEIGHTS: Record<RuleId, number> = {
  DQ1: 3,
  DQ2: 4,
  A1: 1,
  A2: 2,
  A3: 2,
  A4: 2,
  A5: 1,
  B1a: 12,
  B1b: 34,
  B2: 14,
  B4: 11,
  C1: 9,
  C2: 8,
};

interface RuleFieldFixture {
  fieldChanged: string;
  triggered: (rand: () => number) => string;
  expected: string;
}

const RULE_FIELDS: Record<RuleId, RuleFieldFixture> = {
  DQ1: {
    fieldChanged: "submit_amount",
    triggered: () => "0",
    expected: "> 0 on an approved deal",
  },
  DQ2: {
    fieldChanged: "fico_avg",
    triggered: (r) => (r() > 0.5 ? "(blank)" : "0"),
    expected: "300–850 when guarantors > 0",
  },
  A1: {
    fieldChanged: "bankruptcy",
    triggered: () => "B (open)",
    expected: "no open bankruptcy flag",
  },
  A2: {
    fieldChanged: "original_term",
    triggered: (r) => `${90 + Math.floor(r() * 18)} months`,
    expected: "≤ 84 months (cranes up to 120)",
  },
  A3: {
    fieldChanged: "booking_amount",
    triggered: (r) => `${(112 + r() * 14).toFixed(1)}% of approval`,
    expected: "≤ 110% of approval amount",
  },
  A4: {
    fieldChanged: "booking_date",
    triggered: (r) => `${95 + Math.floor(r() * 60)} days after last approval`,
    expected: "≤ 90 days after last approval",
  },
  A5: {
    fieldChanged: "risk_rating_desc",
    triggered: () => "(null)",
    expected: "a populated risk rating",
  },
  B1a: {
    fieldChanged: "fico_avg",
    triggered: (r) => `${villain(r, 560, 645)}`,
    expected: "at or above the PG FICO grid floor",
  },
  B1b: {
    fieldChanged: "risk_grade",
    triggered: (r) => `${r() > 0.5 ? "A1" : "B1"} at ${(0.3 + r() * 1.6).toFixed(1)} yrs tenure`,
    expected: "A1 / B1 only at ≥ 2 years time-in-business",
  },
  B2: {
    fieldChanged: "pg",
    triggered: (r) => `Corp-only, high credit ${currency(villain(r, 18000, 70000))}`,
    expected: "PG required when high credit < submit amount",
  },
  B4: {
    fieldChanged: "paynet_score",
    triggered: (r) => `${villain(r, 480, 590)}`,
    expected: "at or above the PayNet floor for this grade",
  },
  C1: {
    fieldChanged: "documentation_status",
    triggered: (r) => `${r() > 0.5 ? "Missing financials" : "Stale financials"}`,
    expected: "complete documentation package at funding",
  },
  C2: {
    fieldChanged: "equipment_class",
    triggered: (r) => `${r() > 0.5 ? "Restricted class" : "Unclassified"}`,
    expected: "an approved equipment classification",
  },
};

function villain(rand: () => number, min: number, max: number) {
  return Math.round(min + rand() * (max - min));
}

function currency(value: number) {
  return `$${value.toLocaleString("en-US")}`;
}

const RISK_TIERS: RiskTier[] = ["A", "B1", "B2", "B3"];

function weightedRuleIds(): RuleId[] {
  const pool: RuleId[] = [];
  for (const [ruleId, weight] of Object.entries(RULE_WEIGHTS) as [RuleId, number][]) {
    for (let i = 0; i < weight; i += 1) pool.push(ruleId);
  }
  return pool;
}

function ruleTierOf(ruleId: RuleId) {
  return RULE_DEFINITIONS.find((d) => d.ruleId === ruleId)?.ruleTier ?? "B";
}

const ROW_COUNT = 150;

function generateRows(officeName: string): ExceptionLogRow[] {
  const officeSeed = Array.from(officeName).reduce((acc, ch) => acc * 31 + ch.charCodeAt(0), 7);
  const rand = makeRandom(20260521 + (officeSeed % 100000));
  const pool = weightedRuleIds();
  const now = new Date();

  const rows: ExceptionLogRow[] = Array.from({ length: ROW_COUNT }, (_, i) => {
    const ruleId = pool[Math.floor(rand() * pool.length)]!;
    const fixture = RULE_FIELDS[ruleId];

    // Age spread across the last ~90 days of live capture.
    const ageDays = Math.floor(rand() * 88);
    const timestampDate = subHours(subDays(now, ageDays), Math.floor(rand() * 10) + 1);

    // Reconciliation lags capture: recent rows skew candidate, older confirmed.
    const confirmedLikelihood = Math.min(0.92, 0.08 + (ageDays / 88) * 0.9);
    const confirmed = rand() < confirmedLikelihood;

    const reconciledAtDate = confirmed
      ? subDays(now, Math.max(0, Math.floor(ageDays * (0.15 + rand() * 0.4))))
      : null;

    const riskTier = RISK_TIERS[Math.floor(rand() * RISK_TIERS.length)]!;
    const pg = ruleId === "B2" ? false : ruleId === "B1a" ? true : rand() < 0.58;

    return {
      applicationId: `APP-${(104200 + i * 7 + Math.floor(rand() * 5)).toString()}`,
      officeName,
      ruleId,
      fieldChanged: fixture.fieldChanged,
      valueThatTriggeredFlag: fixture.triggered(rand),
      policyThresholdExpected: fixture.expected,
      submitAmount: Math.round((12000 + rand() * 620000) / 500) * 500,
      riskTier,
      pg,
      analystUserId: `U-${1000 + Math.floor(rand() * 46)}`,
      timestamp: timestampDate.toISOString(),
      reconciliationStatus: confirmed ? "confirmed" : "candidate",
      daysUnreconciled: confirmed ? null : differenceInCalendarDays(now, timestampDate),
      reconciledAt: reconciledAtDate ? reconciledAtDate.toISOString() : null,
      reconciledNote: confirmed ? confirmationNote(ruleId, rand) : null,
    } satisfies ExceptionLogRow;
  });

  return rows.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
}

function confirmationNote(ruleId: RuleId, rand: () => number) {
  const notes = [
    "Reconciled in LeaseWorks — credit committee approval on file.",
    "Confirmed exception; documented rationale attached to the credit memo.",
    "Reconciled: PASS-score approval, no policy departure after review.",
    "Confirmed after underwriter review; compensating factors noted.",
  ];
  if (ruleId === "B1b" && rand() < 0.6) {
    return "Reconciled against PASS-score approvals — auto-rated A1 under $100k, not a true exception.";
  }
  return notes[Math.floor(rand() * notes.length)]!;
}

/**
 * Lightweight local mock store — direct edit, no propose/approve queue.
 * Rows persist for the browser session only.
 */
const stores = new Map<string, ExceptionLogRow[]>();

function getStore(officeName: string) {
  let rows = stores.get(officeName);
  if (!rows) {
    rows = generateRows(officeName);
    stores.set(officeName, rows);
  }
  return rows;
}

export interface ReconcileInput {
  applicationId: string;
  reconciledNote: string;
}

/** Marks a row confirmed immediately — no review step, no approval queue. */
export function markRowReconciled({ applicationId, reconciledNote }: ReconcileInput) {
  for (const [officeName, rows] of stores) {
    if (!rows.some((row) => row.applicationId === applicationId)) continue;
    stores.set(
      officeName,
      rows.map((row) =>
        row.applicationId === applicationId
          ? {
              ...row,
              reconciliationStatus: "confirmed" as const,
              reconciledAt: new Date().toISOString(),
              daysUnreconciled: null,
              reconciledNote,
            }
          : row,
      ),
    );
  }
}

function emptySummary(): ExceptionsLogSummary {
  return {
    dataAvailable: false,
    openTierACount: 0,
    avgDaysUnreconciledTierA: 0,
    rows: [],
  };
}

function generateSummary(
  offices: OfficeMapping[],
  params: { office?: string },
): ExceptionsLogSummary {
  const office = params.office ?? "all";
  const dataAvailable = officeDataAvailable(offices, office);

  if (!dataAvailable) return emptySummary();

  const scoped = isAllOffices(office)
    ? liveOffices(offices)
    : liveOffices(offices).filter((o) => o.officeName === office);

  const rows = scoped
    .flatMap((o) => getStore(o.officeName))
    .sort((a, b) => b.timestamp.localeCompare(a.timestamp));

  // Summary chip is Tier A candidates only — page-level filters never touch it.
  const openTierA = rows.filter(
    (r) => r.reconciliationStatus === "candidate" && ruleTierOf(r.ruleId) === "A",
  );
  const avgDays = openTierA.length
    ? openTierA.reduce((sum, r) => sum + (r.daysUnreconciled ?? 0), 0) / openTierA.length
    : 0;

  return {
    dataAvailable: true,
    openTierACount: openTierA.length,
    avgDaysUnreconciledTierA: Number(avgDays.toFixed(1)),
    rows: [...rows],
  };
}

export async function fetchExceptionsLogSummary(
  offices: OfficeMapping[],
  params: { range?: string; office?: string; compare?: boolean },
) {
  if (mockFor("/exceptions-log/summary")) return mockResponse(generateSummary(offices, params));

  return apiGet<ExceptionsLogSummary>("/exceptions-log/summary", {
    range: params.range,
    office: params.office,
    compare: params.compare ? "true" : undefined,
  });
}

export function useExceptionsLogSummary(
  params: { range?: string; office?: string; compare?: boolean } = {},
) {
  const { data: offices, isError: officesFailed } = useOfficeMapping();

  return useQuery({
    queryKey: queryKey(LOG_QUERY_SCOPE, { ...params, offices: officesFingerprint(offices) }),
    queryFn: () => fetchExceptionsLogSummary(offices ?? [], params),
    enabled: officesSettled(offices, officesFailed),
    staleTime: 60 * 1000,
  });
}

/**
 * Result of a live reconcile. The endpoint returns the application's rows in their
 * new state so the drawer can update without refetching the whole (unpaginated) log.
 */
export interface ReconcileResult {
  applicationId: string;
  /**
   * How many (application, rule) pairs this call confirmed. The endpoint takes no
   * ruleId but one application can breach several rules, so it confirms all of the
   * open ones — matching what the mock store has always done.
   */
  reconciledCount: number;
  rows: ExceptionLogRow[];
}

/** Direct-edit reconciliation — takes effect immediately, no approval queue. */
export function useReconciliationActions() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: ReconcileInput) => {
      const path = `/exceptions-log/${encodeURIComponent(input.applicationId)}/reconcile`;
      if (mockFor(path)) {
        markRowReconciled(input);
        return mockResponse<ReconcileResult>(
          { applicationId: input.applicationId, reconciledCount: 1, rows: [] },
          120,
        );
      }
      // apiPost surfaces the backend's `detail` verbatim, which matters here: a 400
      // says "A reconciliation note is required" and a 404 names the application.
      return apiPost<ReconcileResult>(path, { reconciledNote: input.reconciledNote });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [LOG_QUERY_SCOPE] });
    },
  });

  return {
    markReconciled: (input: ReconcileInput) => mutation.mutateAsync(input),
    isPending: mutation.isPending,
    /** Surfaced so the drawer can show why a reconcile was refused. */
    error: mutation.error as Error | null,
  };
}

/** Today's date, formatted for display in the drawer. */
export function formatLogDate(iso: string) {
  return format(new Date(iso), "MMM d, yyyy · HH:mm");
}
